import type { GapCandidate, GetGapExecutionApiResponse } from '@momentum/types';
import { GapCandidateDisposition, GapRunVerdict } from '@momentum/types';

/**
 * The tallies an execution does not store.
 *
 * A run document is written one per SCOPE, with the run-level fields copied
 * verbatim into each; `gaps`, `candidates` and `coverage` are the only per-scope
 * material. So every count a reader wants about the execution as a whole has to
 * be summed here, and both the inspector rail's summary and the run audit read
 * these functions so the two can never disagree.
 */

const VERDICT_TONE: Record<GapRunVerdict, string> = {
  [GapRunVerdict.CONFIRMED]: 'emerald',
  [GapRunVerdict.WEAKENED]: 'amber',
  [GapRunVerdict.CONTRADICTED]: 'rose',
  [GapRunVerdict.UNVERIFIABLE]: '',
};

/**
 * Stands in for a gap whose validator batch never produced a usable verdict.
 * Lowercase, so it cannot be mistaken for one of the pipeline's own enum values.
 */
export const PENDING_VERDICT = 'pending';

export function verdictTone(verdict: string): string {
  return VERDICT_TONE[verdict as GapRunVerdict] ?? '';
}

export interface VerdictCount {
  verdict: string;
  count: number;
}

export interface ExecutionTotals {
  scopeCount: number;
  gapCount: number;
  candidateCount: number;
  missedSourceCount: number;
  /** In first-seen order, which follows the order the scopes were written. */
  verdictCounts: VerdictCount[];
}

export function totalsOf(
  execution: GetGapExecutionApiResponse
): ExecutionTotals {
  const verdictCounts = new Map<string, number>();
  let gapCount = 0;
  let candidateCount = 0;

  for (const scope of execution.scopes) {
    gapCount += scope.gaps.length;
    candidateCount += scope.candidates.length;

    for (const gap of scope.gaps) {
      const verdict = gap.validation?.verdict ?? PENDING_VERDICT;
      verdictCounts.set(verdict, (verdictCounts.get(verdict) ?? 0) + 1);
    }
  }

  const missedSourceCount = execution.iterations.reduce(
    (runningTotal, iteration) => runningTotal + iteration.missedSources.length,
    0
  );

  return {
    scopeCount: execution.scopes.length,
    gapCount,
    candidateCount,
    missedSourceCount,
    verdictCounts: [...verdictCounts].map(([verdict, count]) => ({
      verdict,
      count,
    })),
  };
}

export interface FunnelCell {
  disposition: GapCandidateDisposition;
  label: string;
  count: number;
  tone: string;
}

const FUNNEL_ROW: {
  disposition: GapCandidateDisposition;
  label: string;
  tone: string;
}[] = [
  {
    disposition: GapCandidateDisposition.PROMOTED,
    label: 'Promoted',
    tone: 'cyan',
  },
  {
    disposition: GapCandidateDisposition.MERGED,
    label: 'Merged',
    tone: 'emerald',
  },
  {
    disposition: GapCandidateDisposition.FILTERED,
    label: 'Filtered',
    tone: '',
  },
  {
    disposition: GapCandidateDisposition.DISCARDED,
    label: 'Discarded',
    tone: '',
  },
];

/**
 * What the merge stage did with every candidate both finder families proposed,
 * in funnel order. Always four cells: a disposition nothing took is a fact about
 * the run, not a cell to omit.
 */
export function candidateFunnel(
  execution: GetGapExecutionApiResponse
): FunnelCell[] {
  const counts = new Map<GapCandidateDisposition, number>();

  for (const scope of execution.scopes) {
    for (const candidate of scope.candidates) {
      counts.set(
        candidate.disposition,
        (counts.get(candidate.disposition) ?? 0) + 1
      );
    }
  }

  return FUNNEL_ROW.map((cell) => ({
    ...cell,
    count: counts.get(cell.disposition) ?? 0,
  }));
}

export interface DroppedCandidate {
  scopeName: string;
  candidate: GapCandidate;
}

/**
 * The candidates that never reached adjudication.
 *
 * These are the rows the funnel exists for: a dropped candidate is the only
 * carrier of `dispositionReason`, so it is the only place the pipeline records
 * why a finder's proposal did not become a gap.
 */
export function droppedCandidates(
  execution: GetGapExecutionApiResponse
): DroppedCandidate[] {
  return execution.scopes.flatMap((scope) =>
    scope.candidates
      .filter(
        (candidate) =>
          candidate.disposition === GapCandidateDisposition.FILTERED ||
          candidate.disposition === GapCandidateDisposition.DISCARDED
      )
      .map((candidate) => ({ scopeName: scope.scopeName, candidate }))
  );
}
